
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import TableFilterable from '@/components/reports-v3/components/filterables/TableFilterable.vue';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import {
    PermissionsGroup,
} from '@/store/models.def';
import { TableItemFormatter } from '@/components/reports-v3/components/elements/charts/helpers/tableItemFormatter';
import moment, {MomentInput} from 'moment/moment';
import { getTableName, TIMEZONE_OFFSET_MS } from '@/worker/db/sqlHelpers';

@Component({
    components: {
        TableFilterable,
    },
})
export default class BpProfitLossMonthlyComparisonComparison extends FilterWidget {
    public get permissionIds(): PermissionsGroup[] {
        return ['transactions'];
    }

    public get dateFormatted() {
        return this.selectedDateRange;
    }

    public get expensiveHook() {
        const {
            selectedProjects,
            selectedAsOfDate,
            selectedDateRange,
        } = this;
        return JSON.stringify([
            selectedProjects,
            selectedAsOfDate,
            selectedDateRange,
        ]);
    }

    public filterIds: Array<
        | 'date'
        | 'dateAsOf'
        | 'dateRange'
        | 'stockItems'
        | 'agents'
        | 'customers'
        | 'suppliers'
        | 'project'
    > = [];

    public tableItems: any[] = [];

    public tableFields: any[] = [];
    public detailFields: any[] = [];

    public exportName(range: number[]) {
        return 'P&L Monthly Breakdown' + ' from ' +
            moment(range[0] as MomentInput).format('DD MMM YY') +
            ' - ' +
            moment(range[1] as MomentInput).format('DD MMM YY');
    }

    public getMonthYearArray(startDate, endDate) {
        const start = moment(startDate);
        const end = moment(endDate);

        const monthYearArray: string[] = [];

        while (start.isBefore(end) || start.isSame(end, 'month')) {
            monthYearArray.push(start.format('MMM_YY'));
            start.add(1, 'month');
        }
        return monthYearArray;
    }

    public openingStockDateRange(
        dateRange: [number, number],
    ): [number, number] {
        return [
            moment(dateRange[0]).add(-1, 'month').valueOf(),
            moment(dateRange[1]).add(-1, 'month').valueOf(),
        ];
    }

    public async expensiveCalc() {
        this.tableItems = [];
        this.detailFields = [];
        this.tableFields = [];

        const escapeStr = (s: string) => s.replace(/'/g, '\'\'');
        const table = getTableName('transactions');
        const stockTable = getTableName('stockBalances');
        const range = this.selectedDateRange;
        const openRange = this.openingStockDateRange(
            this.selectedDateRange as [number, number],
        );
        const amt = 'TRY_CAST("AMOUNT" AS DOUBLE)';
        const dateExpr = 'TRY_CAST("DOCDATE" AS BIGINT)';
        const yearExpr = `EXTRACT(YEAR FROM EPOCH_MS(`
            + `${dateExpr} + ${TIMEZONE_OFFSET_MS}))`;
        const monthExpr = `EXTRACT(MONTH FROM EPOCH_MS(`
            + `${dateExpr} + ${TIMEZONE_OFFSET_MS}))`;

        let projectFilter = '';
        if (this.selectedProjects.length > 0) {
            const list = this.selectedProjects
                .map((p) => `'${escapeStr(p)}'`).join(',');
            projectFilter = ` AND "PROJECT" IN (${list})`;
        }

        const allMonthYears = this.getMonthYearArray(
            range[0], range[1],
        );

        // Build months array for lookups
        const startMoment = moment(range[0]).startOf('month');
        const endMoment = moment(range[1]).endOf('month');
        const months: Array<{
            year: number; month: number;
        }> = [];
        const mm = startMoment.clone();
        while (mm.isSameOrBefore(endMoment, 'month')) {
            months.push({
                year: mm.year(),
                month: mm.month() + 1,
            });
            mm.add(1, 'month');
        }

        // Precompute opening stock keys for each month
        const openKeys = months.map((m) => {
            const prev = moment({
                year: m.year, month: m.month - 1,
            }).subtract(1, 'month');
            return `${prev.year()}-${prev.month() + 1}`;
        });

        const jobId = this.currentJobId;
        const queryHead = await this._ensureTablesLoaded(
            ['transactions', 'stockBalances'],
        );
        this.checkInterrupted(jobId);

        this.generateLoadingText(0.3);

        const accTypes
            = `'SL','SA','CO','OI','EO','EP'`;

        const [detailResult, openStockResult, closeStockResult] =
            await Promise.all([
                // Detail: accType x specialAccTypeName x month
                queryHead.rawQuery(`
                    SELECT "ACCTYPE",
                        "SPECIALACCTYPENAME" as name,
                        ${yearExpr} as year,
                        ${monthExpr} as month,
                        SUM(${amt}) as total
                    FROM ${table}
                    WHERE ${dateExpr}
                        BETWEEN ${range[0]} AND ${range[1]}
                        AND "ACCTYPE" IN (${accTypes})
                        ${projectFilter}
                    GROUP BY "ACCTYPE", name,
                        year, month
                `),
                // Opening stock by month
                queryHead.rawQuery(`
                    SELECT "SYEAR" as year,
                        "SMONTH" as month,
                        SUM(${amt}) as total
                    FROM ${stockTable}
                    WHERE ${dateExpr}
                        BETWEEN ${openRange[0]}
                        AND ${openRange[1]}
                        ${projectFilter}
                    GROUP BY "SYEAR", "SMONTH"
                `),
                // Closing stock by month
                queryHead.rawQuery(`
                    SELECT "SYEAR" as year,
                        "SMONTH" as month,
                        SUM(${amt}) as total
                    FROM ${stockTable}
                    WHERE ${dateExpr}
                        BETWEEN ${range[0]}
                        AND ${range[1]}
                        ${projectFilter}
                    GROUP BY "SYEAR", "SMONTH"
                `),
            ]);
        this.checkInterrupted(jobId);

        // Build lookups from detail result
        // Monthly by accType: accType-year-month -> total
        const monthlyByAcc: Record<string, number> = {};
        // Detail: accType-name-year-month -> total
        const detailByAccName: Record<string, number> = {};
        // Detail totals: accType-name -> total
        const detailTotals: Record<string, number> = {};
        // Names per accType (preserving insertion order)
        const namesByAcc: Record<string, string[]> = {};
        const namesSeen: Record<string, Set<string>> = {};

        for (const row of detailResult) {
            const acc = String(row.ACCTYPE);
            const name = String(row.name);
            const y = Number(row.year);
            const m = Number(row.month);
            const total = Number(row.total) || 0;

            const accKey = `${acc}-${y}-${m}`;
            monthlyByAcc[accKey] =
                (monthlyByAcc[accKey] || 0) + total;

            const detailKey = `${acc}-${name}-${y}-${m}`;
            detailByAccName[detailKey] =
                (detailByAccName[detailKey] || 0) + total;

            const totalKey = `${acc}-${name}`;
            detailTotals[totalKey] =
                (detailTotals[totalKey] || 0) + total;

            if (!namesSeen[acc]) {
                namesSeen[acc] = new Set();
                namesByAcc[acc] = [];
            }
            if (!namesSeen[acc].has(name)) {
                namesSeen[acc].add(name);
                namesByAcc[acc].push(name);
            }
        }

        // Stock lookups
        const openStockMap: Record<string, number> = {};
        for (const row of openStockResult) {
            const key = `${Number(row.year)}`
                + `-${Number(row.month)}`;
            openStockMap[key] = Number(row.total) || 0;
        }
        const closeStockMap: Record<string, number> = {};
        for (const row of closeStockResult) {
            const key = `${Number(row.year)}`
                + `-${Number(row.month)}`;
            closeStockMap[key] = Number(row.total) || 0;
        }

        this.generateLoadingText(0.7);

        const newTable: any[] = [];

        // Helper: get monthly total for an accType
        const getVal = (acc: string, i: number): number => {
            const m = months[i];
            return monthlyByAcc[
                `${acc}-${m.year}-${m.month}`
            ] || 0;
        };

        // Helper: build detail items for an accType
        const buildItems = (
            acc: string, sign: number,
        ): any[] => {
            const names = namesByAcc[acc] || [];
            return names.map((name) => {
                const obj: any = {
                    total: (detailTotals[
                        `${acc}-${name}`
                    ] || 0) * sign,
                    particular: name,
                };
                allMonthYears.forEach((my, i) => {
                    const m = months[i];
                    const key = `${acc}-${name}`
                        + `-${m.year}-${m.month}`;
                    obj[my] = (detailByAccName[key]
                        || 0) * sign;
                });
                return obj;
            });
        };

        // Revenue (SL)
        const saleObj: any = {
            total: 0, items: [],
            particular: 'Revenue',
        };
        saleObj.total = months.reduce(
            (sum, m, i) => sum + getVal('SL', i), 0,
        ) * -1;
        allMonthYears.forEach((my, i) => {
            saleObj[my] = getVal('SL', i) * -1;
        });
        saleObj.items = buildItems('SL', -1);
        newTable.push(saleObj);

        // Revenue Adjustment (SA)
        const salesAdjustmentObj: any = {
            total: 0, items: [],
            particular: 'Revenue Adjustment',
        };
        salesAdjustmentObj.total = months.reduce(
            (sum, m, i) => sum + getVal('SA', i), 0,
        ) * -1;
        allMonthYears.forEach((my, i) => {
            salesAdjustmentObj[my] = getVal('SA', i) * -1;
        });
        // SA detail NOT negated (matching original)
        salesAdjustmentObj.items = buildItems('SA', 1);
        newTable.push(salesAdjustmentObj);

        // Net Revenue (SL + SA)
        const netSalesObj: any = {
            total: 0,
            particular: 'Net Revenue',
        };
        netSalesObj.total = months.reduce(
            (sum, m, i) => sum
                + getVal('SL', i) + getVal('SA', i), 0,
        ) * -1;
        allMonthYears.forEach((my, i) => {
            netSalesObj[my] =
                (getVal('SL', i)
                    + getVal('SA', i)) * -1;
        });
        newTable.push(netSalesObj);

        // COGS (CO)
        const COGSObj: any = {
            total: 0, items: [],
            particular: 'COGS',
        };
        COGSObj.total = months.reduce(
            (sum, m, i) => sum + getVal('CO', i), 0,
        );
        allMonthYears.forEach((my, i) => {
            const m = months[i];
            let val = getVal('CO', i);
            // Stock adjustment per month
            val += openStockMap[openKeys[i]] || 0;
            const closeKey = `${m.year}-${m.month}`;
            val -= closeStockMap[closeKey] || 0;
            COGSObj[my] = val;
        });
        COGSObj.items = buildItems('CO', 1);

        // Opening Stock detail item
        const openingStockObj: any = {
            particular: 'Opening Stocks',
            total: 0,
        };
        allMonthYears.forEach((my, i) => {
            openingStockObj[my] =
                openStockMap[openKeys[i]] || 0;
        });
        COGSObj.items.unshift(openingStockObj);

        // Closing Stock detail item
        const closingStockObj: any = {
            particular: 'Closing Stocks',
            total: 0,
        };
        allMonthYears.forEach((my, i) => {
            const m = months[i];
            const closeKey = `${m.year}-${m.month}`;
            closingStockObj[my] =
                closeStockMap[closeKey] || 0;
        });
        COGSObj.items.push(closingStockObj);

        newTable.push(COGSObj);

        // Gross Profit (SL + SA + CO), negated + stock
        const grossProfitObj: any = {
            total: 0,
            particular: 'Gross Profit',
        };
        grossProfitObj.total = months.reduce(
            (sum, m, i) => sum + getVal('SL', i)
                + getVal('SA', i) + getVal('CO', i), 0,
        ) * -1;
        allMonthYears.forEach((my, i) => {
            const m = months[i];
            let val = (getVal('SL', i)
                + getVal('SA', i)
                + getVal('CO', i)) * -1;
            val -= openStockMap[openKeys[i]] || 0;
            const closeKey = `${m.year}-${m.month}`;
            val += closeStockMap[closeKey] || 0;
            grossProfitObj[my] = val;
        });
        newTable.push(grossProfitObj);

        // Other Income (OI)
        const otherIncomeObj: any = {
            total: 0, items: [],
            particular: 'Other Income',
        };
        otherIncomeObj.total = months.reduce(
            (sum, m, i) => sum + getVal('OI', i), 0,
        ) * -1;
        allMonthYears.forEach((my, i) => {
            otherIncomeObj[my] = getVal('OI', i) * -1;
        });
        otherIncomeObj.items = buildItems('OI', -1);
        newTable.push(otherIncomeObj);

        // Extra Ordinary Income (EO)
        const extraOrdinaryIncomeObj: any = {
            total: 0, items: [],
            particular: 'Extra Ordinary Income',
        };
        extraOrdinaryIncomeObj.total = months.reduce(
            (sum, m, i) => sum + getVal('EO', i), 0,
        ) * -1;
        allMonthYears.forEach((my, i) => {
            extraOrdinaryIncomeObj[my] =
                getVal('EO', i) * -1;
        });
        extraOrdinaryIncomeObj.items = buildItems('EO', -1);
        newTable.push(extraOrdinaryIncomeObj);

        // Expenses (EP)
        const expensesObj: any = {
            total: 0, items: [],
            particular: 'Expenses',
        };
        expensesObj.total = months.reduce(
            (sum, m, i) => sum + getVal('EP', i), 0,
        );
        allMonthYears.forEach((my, i) => {
            expensesObj[my] = getVal('EP', i);
        });
        expensesObj.items = buildItems('EP', 1);
        newTable.push(expensesObj);

        // Net Profit (all accTypes), negated + stock
        const netProfitObj: any = {
            total: 0,
            particular: 'Net Profit',
        };
        netProfitObj.total = months.reduce(
            (sum, m, i) => sum + getVal('SL', i)
                + getVal('SA', i) + getVal('CO', i)
                + getVal('OI', i) + getVal('EO', i)
                + getVal('EP', i), 0,
        ) * -1;
        allMonthYears.forEach((my, i) => {
            const m = months[i];
            let val = (getVal('SL', i)
                + getVal('SA', i) + getVal('CO', i)
                + getVal('OI', i) + getVal('EO', i)
                + getVal('EP', i)) * -1;
            val -= openStockMap[openKeys[i]] || 0;
            const closeKey = `${m.year}-${m.month}`;
            val += closeStockMap[closeKey] || 0;
            netProfitObj[my] = val;
        });

        // Total stock adjustments
        const closingStockTotal = Object.values(
            closeStockMap,
        ).reduce((a, b) => a + b, 0);
        const openingStockTotal = Object.values(
            openStockMap,
        ).reduce((a, b) => a + b, 0);

        closingStockObj.total = closingStockTotal;
        openingStockObj.total = openingStockTotal;

        COGSObj.total += openingStockTotal
            - closingStockTotal;
        grossProfitObj.total += closingStockTotal
            - openingStockTotal;
        netProfitObj.total += closingStockTotal
            - openingStockTotal;

        newTable.push(netProfitObj);
        this.tableItems = newTable;

        this.tableFields = allMonthYears.map((m) => {
            return {
                key: m,
                sortable: false,
                formatter: TableItemFormatter.currencySplit,
            };
        });

        this.tableFields.unshift({
            key: 'total',
            sortable: false,
            formatter: TableItemFormatter.currencySplit,
        });

        this.tableFields.unshift({
            key: 'particular',
            sortable: false,
        });

        this.detailFields = allMonthYears.map((m) => {
            return {
                key: m,
                sortable: false,
                formatter: TableItemFormatter.currencySplit,
            };
        });

        this.detailFields.unshift({
            key: 'total',
            sortable: false,
            formatter: TableItemFormatter.currencySplit,
        });

        this.detailFields.unshift({
            key: 'particular',
            sortable: false,
        });
    }
}
